import { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { Maximize, X } from 'lucide-react';
import { useContainerDimensions } from '../hooks/useContainerDimensions';
import { AnnotationLayer, type AnnotationTask, type NewTaskClickData } from './AnnotationLayer';
import type { LineType } from '../lib/lineMath';
import { containRect, type MediaLayout, type TaskGeometry } from '../lib/annotationGeometry';

/** Data produced when the user clicks/draws on the image */
export interface ImageNewTaskData {
  /** Natural image pixel coordinates */
  coord: { left: number; top: number };
  /** Percentage coordinates (0–100) for positioning UI */
  percentX: number;
  percentY: number;
  /** Point and line as fractions of the image: what gets stored */
  geometry: TaskGeometry;
  /** Box pixels of the drawn line (for the preview while the form is open) */
  coords?: [number, number][] | null;
  /** Line type if drawn */
  taskType?: LineType;
  /** Container dimensions at time of drawing (for line preview) */
  containerWidth?: number;
  containerHeight?: number;
}

interface ImageViewerProps {
  resource: {
    id: string;
    signedUrl?: string;
    thumbnailUrl?: string;
    [k: string]: unknown;
  };
  tasks: AnnotationTask[];
  selectedTask?: AnnotationTask | null;
  updateSelectedTask: (task: AnnotationTask | null) => void;
  messagesTabOpen: boolean;
  userRole: string | null;
  onCreateTask?: (data: ImageNewTaskData) => void;
  /** Reports where the image sits in its container (for positioning the task popover) */
  onLayoutChange?: (layout: MediaLayout | null) => void;
}

const sameLayout = (a: MediaLayout | null, b: MediaLayout | null) =>
  !!a && !!b &&
  a.box.width === b.box.width && a.box.height === b.box.height &&
  a.media.left === b.media.left && a.media.top === b.media.top &&
  a.media.width === b.media.width && a.media.height === b.media.height &&
  a.natural.width === b.natural.width && a.natural.height === b.natural.height;

/** Where an image is drawn inside its container, measured after layout and on every resize. */
function useImageLayout(
  boxRef: React.RefObject<HTMLElement | null>,
  imgRef: React.RefObject<HTMLImageElement | null>,
  deps: unknown[],
): MediaLayout | null {
  const [layout, setLayout] = useState<MediaLayout | null>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const img = imgRef.current;
    if (!box || !img) {
      setLayout(null);
      return;
    }
    const measure = () => {
      const b = box.getBoundingClientRect();
      const r = img.getBoundingClientRect();
      if (!img.naturalWidth || !img.naturalHeight || !b.width || !r.width || !r.height) {
        setLayout(null);
        return;
      }
      const natural = { width: img.naturalWidth, height: img.naturalHeight };
      // The element keeps the image's aspect ratio; containRect also covers object-contain letterboxing
      const inner = containRect({ width: r.width, height: r.height }, natural);
      const next: MediaLayout = {
        box: { width: b.width, height: b.height },
        media: { left: r.left - b.left + inner.left, top: r.top - b.top + inner.top, width: inner.width, height: inner.height },
        natural,
      };
      setLayout((prev) => (sameLayout(prev, next) ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(img);
    return () => observer.disconnect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return layout;
}

export function ImageViewer({
  resource,
  tasks,
  selectedTask,
  updateSelectedTask,
  messagesTabOpen,
  userRole,
  onCreateTask,
  onLayoutChange,
}: ImageViewerProps) {
  const { ref: containerRef, dimensions } = useContainerDimensions();
  const imgRef = useRef<HTMLImageElement>(null);
  const [fullScreen, setFullScreen] = useState(false);

  // Track which URL has loaded or failed instead of resetting flags in an effect. The effect ran after mount and
  // undid onLoad for images served from the browser cache, leaving the full image hidden behind the thumbnail.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imageErrored = failedSrc === resource.signedUrl;
  const loading = loadedSrc !== resource.signedUrl;

  const isViewer = userRole === 'viewer' || userRole === 'guest';
  const tasksVisible = !messagesTabOpen;

  // Exit fullscreen on scroll
  useEffect(() => {
    if (!fullScreen) return;
    const onScroll = () => {
      if (window.scrollY > 0) setFullScreen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fullScreen]);

  const handleImageLoaded = useCallback(() => {
    setLoadedSrc(resource.signedUrl ?? null);
  }, [resource.signedUrl]);

  const layout = useImageLayout(containerRef, imgRef, [loading, resource.signedUrl]);

  useEffect(() => {
    onLayoutChange?.(layout);
  }, [layout, onLayoutChange]);

  // Image tasks also keep the point in image pixels (coord_x/coord_y), as before
  const handleAnnotationCreate = useCallback(
    (data: NewTaskClickData) => {
      const img = imgRef.current;
      if (!img || !onCreateTask) return;
      onCreateTask({
        coord: {
          left: Math.round(data.geometry.pt[0] * img.naturalWidth),
          top: Math.round(data.geometry.pt[1] * img.naturalHeight),
        },
        percentX: data.coordX,
        percentY: data.coordY,
        geometry: data.geometry,
        coords: data.pixelCoords ?? null,
        taskType: data.taskType,
        containerWidth: data.containerWidth,
        containerHeight: data.containerHeight,
      });
    },
    [onCreateTask],
  );

  const showAnnotations = tasksVisible && !loading && dimensions.width > 0 && !!layout;

  return (
    <>
      {/* Fullscreen overlay: the image only. Task dots and lines show only in the normal view (agreed earlier). */}
      {fullScreen && (
        <div className="fixed inset-0 z-[105] flex items-center justify-center bg-black/90">
          {!imageErrored && resource.signedUrl && (
            <div className="relative max-h-[800px] max-w-[1400px]">
              <img
                src={resource.signedUrl}
                alt="preview"
                className="max-h-[80vh] max-w-[1400px] object-scale-down xl:max-w-[1000px] lg:max-w-[700px] md:max-w-[400px] sm:max-w-[200px]"
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => setFullScreen(false)}
            className="fixed top-8 right-8 flex size-8 cursor-pointer items-center justify-center rounded hover:scale-105 transition-transform"
          >
            <X className="size-8 text-white" />
          </button>
        </div>
      )}

      {/* Regular view */}
      <div
        ref={containerRef as React.RefObject<HTMLDivElement>}
        className="relative mx-auto flex h-full max-h-[calc(35rem-2px)] max-w-full items-center justify-center overflow-hidden"
      >
        {!imageErrored && resource.signedUrl && (
          <>
            {/* Thumbnail (shown while full image loads) */}
            {resource.thumbnailUrl && (
              <img
                src={resource.thumbnailUrl}
                alt="thumb"
                className={loading ? 'block max-h-full max-w-full object-contain' : 'hidden'}
              />
            )}

            {/* Full image */}
            <img
              ref={imgRef}
              src={resource.signedUrl}
              alt="preview"
              className={`max-h-full max-w-full object-contain ${loading ? 'hidden' : 'block'}`}
              onLoad={handleImageLoaded}
              onError={() => setFailedSrc(resource.signedUrl ?? null)}
            />
          </>
        )}

        {/* Annotations (unified via AnnotationLayer) */}
        {showAnnotations && (
          <AnnotationLayer
            containerRef={containerRef}
            dimensions={dimensions}
            tasks={tasks}
            selectedTask={selectedTask}
            onSelectTask={updateSelectedTask}
            onCreateTask={onCreateTask ? handleAnnotationCreate : undefined}
            canCreate={!isViewer && !!onCreateTask}
            mediaType="image"
            tasksVisible={tasksVisible}
            layout={layout}
          />
        )}

        {/* Fullscreen toggle */}
        <button
          type="button"
          onClick={() => setFullScreen((v) => !v)}
          className="absolute bottom-2.5 right-2.5 z-[3] flex size-6 cursor-pointer items-center justify-center rounded-sm bg-black/60 hover:scale-105 transition-transform"
        >
          <Maximize className="size-4 text-white" />
        </button>
      </div>
    </>
  );
}
